import { QueryClientProvider } from '@tanstack/react-query'
import { CosmicLocalizationContextProvider } from '@taxbit-private/cosmic-localization'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { OAuthCallback } from 'pages/auth/OAuthCallback'
import OAuthSuccess from 'pages/auth/OAuthSuccess'

import CosmicErrorBoundary from 'components/CosmicErrorBoundary'
import AuthProvider from 'contexts/auth/AuthContext'
import ToastNotifications from 'store/notification-cosmic/ToastNotifications'
import CosmicThemeProvider from 'utils/styles/CosmicThemeProvider'
import initializeVendors from 'vendors/initializeVendors'
import QueryDevtools from 'vendors/query/QueryDevtools'
import tacticQueryClient from 'vendors/query/tacticQueryClient'

import { ApiProvider } from './api/useApi'
import { OrgProvider } from './contexts/OrgContext'
import { UserProvider } from './contexts/UserContext'
import App from './layouts/app/App'
import LoginPage from './layouts/login/LoginPage'

import '@taxbit-private/cosmic/style.css'

/**
 * APP_VERSION is statically replaced at runtime by Vite.
 * Attaching it to the window just gives us an easy means of checking
 * the current version of the frontend app via the dev console.
 */
window.appVersion = APP_VERSION

void (async () => {
  const InitializedVendorProvider = await initializeVendors()

  const root = createRoot(document.querySelector('#root') as HTMLElement)

  root.render(
    <CosmicThemeProvider>
      <CosmicErrorBoundary trackingId="root-error-boundary">
        <InitializedVendorProvider>
          <BrowserRouter future={{ v7_relativeSplatPath: false, v7_startTransition: true }}>
            <ToastNotifications />
            <AuthProvider>
              <QueryClientProvider client={tacticQueryClient}>
                <OrgProvider>
                  <UserProvider>
                    <ApiProvider>
                      <CosmicLocalizationContextProvider
                        initialData={{ locale: 'en-US' }}
                        shouldInjectGlobal={true}
                      >
                        <Routes>
                          <Route path="/callback" element={<OAuthCallback />} />
                          <Route path="/app/*" element={<App />} />
                          <Route path="/login" key="login" element={<LoginPage />} />
                          <Route path="/qb-success" element={<OAuthSuccess isQb={true} />} />
                          <Route path="/exchange-success" element={<OAuthSuccess />} />
                          <Route path="*" element={<Navigate replace={true} to="/login" />} />
                        </Routes>
                      </CosmicLocalizationContextProvider>
                    </ApiProvider>
                  </UserProvider>
                </OrgProvider>
                <QueryDevtools />
              </QueryClientProvider>
            </AuthProvider>
          </BrowserRouter>
        </InitializedVendorProvider>
      </CosmicErrorBoundary>
    </CosmicThemeProvider>
  )
})()
